/**
 * AccessBadge — top-of-page indicator showing tier + reads remaining.
 *
 * - Free:  "Free preview · Unlock full read"
 * - One Full Read:     "1 full read ready" (count-driven)
 * - 14-Day Pass:       "23 reads · 9 days left"
 * - Monthly Membership "62 reads this cycle · resets Mar 14"
 */
import { useMemo } from "react";
import { Sparkles, ShieldCheck } from "lucide-react";

function daysBetween(future) {
  if (!future) return null;
  const ms = new Date(future).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function formatShortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

const TIER_NAMES = {
  one_full_read: "Full Read",
  fourteen_day_pass: "14-Day Pass",
  monthly_membership: "Monthly Membership",
};

export const AccessBadge = ({ access, onOpenPaywall, onOpenRestore, onOpenPortal }) => {
  const isFree = !access || access.tier === "free";
  const isMonthly = access?.tier === "monthly_membership";
  const isPass = access?.tier === "fourteen_day_pass";

  const detail = useMemo(() => {
    if (isFree) return "Unlock full read";
    const reads = access.reads_remaining ?? 0;
    if (isMonthly) {
      const resetOn = formatShortDate(access.period_end);
      return resetOn
        ? `${reads} reads this cycle · resets ${resetOn}`
        : `${reads} reads this cycle`;
    }
    if (isPass) {
      const days = daysBetween(access.expires_at);
      return days != null
        ? `${reads} reads · ${days} day${days === 1 ? "" : "s"} left`
        : `${reads} reads left`;
    }
    // one_full_read
    return reads > 0
      ? `${reads} full read${reads === 1 ? "" : "s"} ready`
      : "Read used · grab another";
  }, [isFree, isMonthly, isPass, access]);

  return (
    <div
      data-testid="access-badge"
      data-tier={access?.tier || "free"}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border"
      style={{
        background: isFree ? "var(--ss-bg-deep)" : "var(--ss-green-bg)",
        borderColor: isFree ? "var(--ss-rule-line)" : "var(--ss-green-rail)",
        color: "var(--ss-ink)",
      }}
    >
      {isFree ? (
        <Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} style={{ color: "var(--ss-gold)" }} />
      ) : (
        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.25} style={{ color: "var(--ss-green)" }} />
      )}
      <span className="ss-mono uppercase tracking-wider text-[10px]" style={{ color: "var(--ss-muted)" }}>
        {isFree ? "Free preview" : TIER_NAMES[access.tier] || "Paid"}
      </span>
      <span className="font-medium" data-testid="access-badge-detail">{detail}</span>

      {isFree ? (
        <button
          onClick={onOpenPaywall}
          data-testid="access-badge-upgrade-btn"
          className="ml-1 text-[12px] font-semibold underline-offset-2 hover:underline"
          style={{ color: "var(--ss-gold-deep)" }}
        >
          Unlock
        </button>
      ) : isMonthly ? (
        <button
          onClick={onOpenPortal}
          data-testid="access-badge-portal-btn"
          className="ml-1 text-[12px] font-semibold underline-offset-2 hover:underline"
          style={{ color: "var(--ss-gold-deep)" }}
        >
          Manage
        </button>
      ) : null}

      <button
        onClick={onOpenRestore}
        data-testid="access-badge-restore-btn"
        className="text-[12px] underline-offset-2 hover:underline"
        style={{ color: "var(--ss-muted)" }}
        title="Restore access on this device"
      >
        Restore
      </button>
    </div>
  );
};
